import { Box } from '@mui/material';
import { Header } from 'components/home';
import { StepperHorizontal } from 'components/shared/stepper_horizontal';
import { TopNavBar } from 'components/top_nav_bar';
import { MenuProvider } from 'contexts/menu/menu_context';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Step1 from './Step1';
import Step2 from './Step2';
import useCifaStore from 'stores/cifa/cifa.store';
import { ROUTES } from 'routes/constants';
import { CifaStatusEnum } from 'enum/cifaStatus.enum';
import CifaAPI from 'api/cifa/cifa.api';

export interface FinancialResourceForm {
  tipo: 'Programa' | 'Projeto' | 'Ação/Atividade';
  descricao: string;
  valor_orcado: number;
  valor_liquidado: number;
}

interface StepData {
  recursos: FinancialResourceForm[];
  rcl: number;
}

const parseSafeNumber = (value: string | number | null | undefined): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  const parsed = parseFloat(value);
  return isNaN(parsed) ? 0 : parsed;
};

const FinancialResources: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Extrai o ano da rota, ex: /service_management/cifa-2027 => 2027
  const match = location.pathname.match(/cifa-(\d{4})/);
  const anoReferencia = match ? Number(match[1]) : 2026;

  const steps = ['Cadastro de Recursos', 'Confirmação dos Dados'];
  const [activeStep, setActiveStep] = useState<number>(0);
  const { cifa, setPartialForm } = useCifaStore();
  const [localData, setLocalData] = useState<StepData>({
    recursos: [],
    rcl: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  useEffect(() => {
    if (!cifa.uuid) {
      navigate(ROUTES[`CIFA${anoReferencia}` as keyof typeof ROUTES] as string);
      return;
    }

    setLocalData({
      recursos: cifa.recursos_financeiros_aplicados_detalhamento || [],
      rcl: parseSafeNumber(cifa.receita_corrente_liquida_2025)
    });
  }, [cifa, navigate, anoReferencia]);

  const handleNext = (data: StepData) => {
    setLocalData(data);
    setPartialForm('recursos_financeiros_aplicados_detalhamento', data.recursos);
    setPartialForm('receita_corrente_liquida_2025', data.rcl);
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => setActiveStep(prev => prev - 1);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      setPartialForm('recursos_financeiros_aplicados_detalhamento', localData.recursos);
      setPartialForm('receita_corrente_liquida_2025', localData.rcl);
      setPartialForm('status_aba_5', CifaStatusEnum.APPROVED);

      await CifaAPI.updateFinancialResource(
        cifa.uuid!,
        {
          recursos_financeiros_aplicados_detalhamento: localData.recursos,
          receita_corrente_liquida_2025: localData.rcl
        }
      );

      setSnackbar({
        open: true,
        message: 'Dados salvos com sucesso!',
        severity: 'success'
      });

      setTimeout(() => navigate(ROUTES[`INCLUDE_CIFA_${anoReferencia}` as keyof typeof ROUTES] as string), 1500);
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao salvar os dados. Tente novamente.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MenuProvider>
      <TopNavBar />
      <Header title={`CIFA - Recursos Financeiros - ${anoReferencia}`} showBackButton={true} />

      <Box width='97%' display='flex' flexDirection='column' justifyContent='center' alignItems='center' margin='2rem'>
        <StepperHorizontal activeStep={activeStep} steps={steps} maxWidth='1152px' />

        {activeStep === 0 && (
          <Step1
            initialData={localData}
            onNext={handleNext}
            loading={isSubmitting}
          />
        )}

        {activeStep === 1 && (
          <Step2
            data={localData}
            onBack={handleBack}
            onConfirm={handleSubmit}
            loading={isSubmitting}
          />
        )}
      </Box>
    </MenuProvider>
  );
};

export default FinancialResources;